import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { UserButton, useUser } from '@clerk/clerk-react';

/**
 * Fixed rail width - the page offsets its content by this on desktop, so
 * the column centers in the space between the rail and the window edge.
 */
export const SIDEBAR_WIDTH_CLASS = 'md:ps-[232px]';

interface NavItem {
  label: string;
  icon: ReactNode;
  /** Route for enabled items; disabled items render as "coming soon". */
  to?: string;
  disabled?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Execution Gaps',
    to: '/deal-intelligence',
    icon: (
      <>
        <path d="M3 12a9 9 0 1 0 2.6-6.3L3 8" />
        <path d="M3 3v5h5" />
        <path d="M12 7.5V12l3 2" />
      </>
    ),
  },
  {
    label: 'Deep Insights',
    disabled: true,
    icon: (
      <>
        <path d="M3 3v18h18" />
        <path d="M7 14l3.5-4 3 2.5L20 6" />
      </>
    ),
  },
  {
    label: 'Planning',
    disabled: true,
    icon: (
      <>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </>
    ),
  },
];

function NavIcon({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function Brand() {
  return (
    <div className="flex items-center justify-center gap-2.5">
      <img src="/intera_logo_blue.svg" alt="Intera" className="h-7 w-auto" />
      <span className="text-base font-semibold tracking-tight text-[#0E1B3A]">
        Intera
      </span>
    </div>
  );
}

/**
 * The deal-intelligence navigation - a fixed left rail in the demo's visual
 * language. Only Deal Intelligence is live; Deep Insights and Planning are
 * surfaced as unclickable disabled entries.
 */
export function DealIntelligenceSidebar() {
  const { user } = useUser();
  const reviewerName = user?.fullName ?? 'Reviewer';

  return (
    <aside className="fixed inset-y-0 start-0 z-30 hidden w-[232px] flex-col border-e border-zinc-200 bg-white px-5 py-6 shadow-[1px_0_0_0_rgba(24,24,27,0.02)] md:flex">
      <Brand />

      <nav className="mt-14 flex flex-col gap-2.5">
        {NAV_ITEMS.map((navItem) => {
          if (navItem.disabled || !navItem.to) {
            return (
              <span
                key={navItem.label}
                className="flex cursor-not-allowed items-center gap-3 rounded-xl px-4 py-3 text-[15px] text-zinc-300"
              >
                <NavIcon>{navItem.icon}</NavIcon>
                {navItem.label}
              </span>
            );
          }
          return (
            <Link
              key={navItem.label}
              to={navItem.to}
              className="flex items-center gap-3 rounded-xl bg-[#00AEEF]/10 px-4 py-3 text-[15px] font-medium text-[#0E1B3A]"
            >
              <span className="text-[#00AEEF]">
                <NavIcon>{navItem.icon}</NavIcon>
              </span>
              {navItem.label}
            </Link>
          );
        })}
      </nav>

      {/* Signed-in reviewer pinned to the bottom - Clerk popup for sign-out. */}
      <div className="mt-auto flex items-center gap-3 px-1">
        <UserButton appearance={{ elements: { avatarBox: 'h-9 w-9' } }} />
        <span className="truncate text-sm font-medium text-zinc-800">
          {reviewerName}
        </span>
      </div>
    </aside>
  );
}
